import React, {
  useCallback, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {hooks} from 'farmx-redux-core';
import PropTypes from 'prop-types';
import { WATER_PRESSURE } from 'components/hooks/graph';
import {
  getWaterPressureYminMax
} from '../../helper/graphHelper';
import NewSensorDataChart from './NewSensorDataChart';
import { VAR_WATER_PRESSURE } from './constants';

const { useUnits } = hooks;

export default function WaterPressureChart(props) {
  const {
    sensor, startDate, endDate, cached, updateYminmax, uniformYAxis, yMinMaxMap,
    showXaxis, header
  } = props;
  const { t } = useTranslation();
  const getUserUnits = useUnits();

  // TODO remove after testing
  // console.log('Render WaterpressureChart');

  const globalYMinMax = yMinMaxMap?.[WATER_PRESSURE] ? yMinMaxMap?.[WATER_PRESSURE] : {min: null, max: null};

  // TODO remove after testing
  // console.log(' globalYMinMax', globalYMinMax);
  // console.log('yMinMaxMap WaterpressureChart', yMinMaxMap);

  const yMinMax = useMemo(() => {
    if((globalYMinMax.min || globalYMinMax.min===0) && globalYMinMax.max) return { min: globalYMinMax.min, max: globalYMinMax.max};
    return null;
  }, [globalYMinMax.min, globalYMinMax.max]);

  const headerLabel = getUserUnits(0, 'pounds_per_square_inch', 'pressure').label;

  // TODO remove after testing
  // console.log('yMinMax WaterpressureChart', yMinMax);

  /** ******** Custom Config Section Start*************** */
  const customConfig = useCallback((configObj, seriesData) => {
    // TODO remove after testing
    // console.log('calling customConfig');
    if(configObj?.series?.[0]){
      // const yMinMax = getWaterPressureYminMax(seriesData);
      const { multiplier } = configObj?.series?.[0];
      const waterPressureData = seriesData?.[WATER_PRESSURE];
      const convertedPressureData = waterPressureData?.map((item) => {
        const convertedData = item?.data?.map((d) => {
          const { value } = getUserUnits(d?.[1], 'pounds_per_square_inch', 'pressure', { decimalPlaces: 2 });
          return [d?.[0], value];
        });
        return {...item, data: convertedData }
      })

      const minMaxArr = convertedPressureData?.map((prData) => {
        const dataPoints = prData?.data?.map((d) => d?.[1]) || [];
        const min = Math.min(...dataPoints);
        const max = Math.max(...dataPoints);
        return [min, max]
      })?.flat() || [];

      const yMin = Math.min(...minMaxArr)
      const yMax = Math.max(...minMaxArr)
      updateYminmax(WATER_PRESSURE, yMin * multiplier, yMax * multiplier);

      const label = getUserUnits(yMin, 'pounds_per_square_inch', 'pressure', { decimalPlaces: 2 })?.label;
      const updatedSeries = convertedPressureData.map((item, index) => {
        const dataPoints = item?.data?.map((d) => d?.[1]) || [];
        const min = Math.min(...dataPoints);
        const max = Math.max(...dataPoints);
        return {
          ...configObj?.series?.[0],
          ...{ data: item?.data },
          sensorName: item?.sensor_name,
          name: (`${item?.name || 'Water Pressure'}: ${convertedPressureData
            ?.[index]?.sensor_name || ''}`),
          tooltip: {
            valueDecimals: 3,
            valueSuffix: t(` ${label}`),
          },
          min,
          max,
        }
      });

      return {
        ...configObj,
        series: updatedSeries,
        yAxis: [{
          ...configObj.yAxis[0],
          min: yMin,
          max: yMax
        }],
      };
      // TODO remove after testing
      // console.log('local y min max multiplier', yMinMax.min, yMinMax.max, multiplier);
    }
    return { ...configObj, ...(!showXaxis ? { xAxis: { visible: false } } : {}) };
  }, [updateYminmax]);
    /** ******** Custom Config Section End*************** */

  return (
    <>
      {sensor?.id && <NewSensorDataChart
        sensorName={sensor.name}
        sensorId={sensor.id}
        sensorType={sensor.type}
        sensorIdentifier={sensor.identifier}
        variables={VAR_WATER_PRESSURE}
        startDate={startDate}
        endDate={endDate}
        cached={cached}
        customConfig={customConfig}
        header={header
          ? {title: t('Water Pressure'), units: t(headerLabel), right: []}
          : undefined
        }
        uniformYAxis={uniformYAxis}
        yMinMax={yMinMax}
      />}
      {!sensor && <div className="chart-component-main">
        <div className='chart-loading-container'>
          <div className="chart-loading">
            <span className="chart-loading-inner">{`${t('Sensor Not Available')}`}</span>
          </div>
        </div>
      </div>}
    </>
  );
}

WaterPressureChart.propTypes = {
  sensor: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    type: PropTypes.string,
    identifier: PropTypes.string,
  }),
  startDate: PropTypes.shape({}),
  endDate: PropTypes.shape({}),
  cached: PropTypes.string,
  updateYminmax: PropTypes.func,
  uniformYAxis: PropTypes.bool,
  yMinMaxMap: PropTypes.shape({}),
  header: PropTypes.bool,
  showXaxis: PropTypes.bool,
};

WaterPressureChart.defaultProps = {
  sensor: null,
  startDate: null,
  endDate: null,
  cached: null,
  updateYminmax: () => null,
  uniformYAxis: false,
  yMinMaxMap: null,
  header: true,
  showXaxis: true
};
